<template>
  <div class="payment-page" :dir="isHebrew ? 'rtl' : 'ltr'">
    <title>{{ texts.metaTitle }}</title>
    <SingleRideHeader :presentedText="texts.headerTitle" />
    <b-container>
      <div class="payment-content after_page_header">
        <div class="row justify-content-center">
          <div class="col-lg-10">
            <div class="row">
              <!-- Product Info Column -->
              <div class="col-md-6">
                <div class="product-info-card">
                  <h2>{{ texts.productTitle }}</h2>
                  <p>
                    {{ texts.productIntro }}
                  </p>
                  <div class="panel panel-default">
                    <div class="panel-heading">
                      <strong>{{ texts.productDetailsTitle }}</strong>
                    </div>
                    <div class="panel-body">
                      <p class="lead">{{ product.name }}</p>
                      <p>{{ product.description }}</p>
                      <ul class="list-unstyled">
                        <li>{{ texts.quantityLabel }}: {{ product.quantity }}</li>
                        <li>{{ texts.unitPriceLabel }}: {{ product.unitPrice }} {{ product.currency }}</li>
                      </ul>
                      <b-button variant="link" @click="goHome">{{ texts.backHome }}</b-button>
                    </div>
                  </div>
                  <div v-if="!hasSumitConfig" class="alert alert-warning">
                    {{ texts.missingSumitConfigLine1 }}
                    <code>VUE_APP_SUMIT_COMPANY_ID</code> {{ texts.andConnector }}
                    <code>VUE_APP_SUMIT_API_PUBLIC_KEY</code> {{ texts.missingSumitConfigLine2 }}
                  </div>
                </div>
              </div>

              <!-- Payment Form Column -->
              <div class="col-md-6 payment-form-column">
              <form 
                id="sumit-payment-form" 
                ref="paymentForm"
                data-og="form"
                method="post"
                action="javascript:void(0);"
              >

                  <div class="form-group">
                    <label>{{ texts.fullNameLabel }}</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      name="customer_name" 
                      v-model="customer.name"
                      :placeholder="texts.fullNamePlaceholder" 
                      required
                      @input="clearPaymentError"
                    />
                  </div>

                  <div class="form-group">
                    <label>{{ texts.emailLabel }}</label>
                    <input 
                      type="email" 
                      class="form-control" 
                      name="customer_email" 
                      v-model="customer.email"
                      :placeholder="texts.emailPlaceholder"
                      required
                      @input="clearPaymentError"
                    />
                  </div>

                  <div class="form-group">
                    <label>{{ texts.phoneLabel }}</label>
                    <input 
                      type="tel" 
                      class="form-control" 
                      name="customer_phone" 
                      v-model="customer.phone"
                      :placeholder="texts.phonePlaceholder"
                      required
                      @input="clearPaymentError"
                    />
                  </div>

                  <div class="form-group">
                    <label>{{ texts.cardNumberLabel }}</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      name="cardNumber"
                      autocomplete="cc-number"
                      inputmode="numeric"
                      maxlength="20" 
                      data-og="cardnumber" 
                      :placeholder="texts.cardNumberPlaceholder"
                      @input="clearPaymentError"
                    />
                  </div>

                  <div class="form-group">
                    <label>{{ texts.expirationLabel }}</label>
                  </div>

                  <div class="form-inline">
                    <div class="form-group mr-10">
                      <label>{{ texts.expirationMonthLabel }}</label>
                      <input 
                        type="text" 
                        class="form-control expiration-input" 
                        name="cardExpiryMonth"
                        autocomplete="cc-exp-month"
                        inputmode="numeric"
                        size="4" 
                        maxlength="2" 
                        data-og="expirationmonth" 
                      :placeholder="texts.expirationMonthPlaceholder"
                      @input="clearPaymentError"
                      />
                    </div>
                    <span>/</span>
                    <div class="form-group ml-10">
                      <label>{{ texts.expirationYearLabel }}</label>
                      <input 
                        type="text" 
                        class="form-control expiration-input" 
                        name="cardExpiryYear"
                        autocomplete="cc-exp-year"
                        inputmode="numeric"
                        size="6" 
                        maxlength="4" 
                        data-og="expirationyear" 
                      :placeholder="texts.expirationYearPlaceholder"
                      @input="clearPaymentError"
                      />
                    </div>
                  </div>

                  <div class="form-group">
                    <label>CVV</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      name="cardCvv"
                      autocomplete="cc-csc"
                      inputmode="numeric"
                      size="4" 
                      maxlength="4" 
                      data-og="cvv" 
                      placeholder="CVV"
                      @input="clearPaymentError"
                    />
                  </div>

                  <div class="form-group">
                    <label>{{ texts.citizenIdLabel }}</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      data-og="citizenid" 
                      :placeholder="texts.citizenIdPlaceholder"
                      @input="clearPaymentError"
                    />
                  </div>

                  <div class="cancellation-policy-section mb-3">
                    <b-button 
                      @click="showPolicy = !showPolicy"
                      variant="link" 
                      class="policy-toggle-btn p-0"
                    >
                      {{ texts.policyToggle }}
                      <b-icon-chevron-down v-if="!showPolicy" />
                      <b-icon-chevron-up v-else />
                    </b-button>
                    <div v-show="showPolicy" class="mt-2">
                      <div class="policy-content">
                        <h5>{{ policyContent.title }}</h5>
                        <p v-for="(paragraph, idx) in policyContent.intro" :key="`policy-intro-${idx}`">
                          {{ paragraph }}
                        </p>

                        <div v-for="(section, sIdx) in policyContent.sections" :key="`policy-sec-${sIdx}`">
                          <h6>{{ section.title }}</h6>

                          <p v-for="(paragraph, pIdx) in (section.paragraphs || [])" :key="`policy-sec-${sIdx}-p-${pIdx}`">
                            {{ paragraph }}
                          </p>

                          <ul v-if="section.list && section.list.length">
                            <li v-for="(item, iIdx) in section.list" :key="`policy-sec-${sIdx}-li-${iIdx}`">
                              {{ item }}
                            </li>
                          </ul>

                          <p
                            v-for="(paragraph, pIdx) in (section.paragraphsAfterList || [])"
                            :key="`policy-sec-${sIdx}-pa-${pIdx}`"
                          >
                            {{ paragraph }}
                          </p>

                          <p v-if="section.email">
                            {{ section.emailPrefix }}<a :href="`mailto:${section.email}`">{{ section.email }}</a>{{ section.emailSuffix }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <b-button 
                    type="submit" 
                    variant="primary" 
                    block
                    class="submit-payment-btn"
                    id="submit-payment-btn"
                    :disabled="isProcessing"
                  >
                    <span v-if="!isProcessing">{{ texts.payButton }} {{ product.unitPrice }} {{ product.currency }}</span>
                    <span v-else>{{ texts.processingPayment }}</span>
                </b-button>

                <div v-if="isProcessing" class="processing-indicator mt-2">
                  <b-spinner small label="processing"></b-spinner>
                  <span>{{ texts.processingHint }}</span>
                </div>

                <div 
                  v-if="paymentErrorMessage"
                  class="og-errors alert alert-danger mt-2"
                >
                  {{ paymentErrorMessage }}
                </div>
                </form>

              </div>
            </div>
          </div>
        </div>
        
        <!-- Security Footer -->
        <div class="payment-security-section mt-4">
          <div class="payment-security-icons">
            <div class="security-badge" tabindex="0" :data-security-note="texts.securityNote">
              <svg width="32" height="32" viewBox="0 0 32 32" aria-hidden="true">
                <path d="M16 4l10 4v7c0 6.1-4.4 11.8-10 13-5.6-1.2-10-6.9-10-13V8l10-4z" fill="none" stroke="#7f7a8e" stroke-width="1.8"/>
                <path d="M11 17l3 3 7-7" fill="none" stroke="#7f7a8e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>SSL Secure</span>
            </div>
            <div class="security-badge" tabindex="0" :data-security-note="texts.securityNote">
              <svg width="32" height="32" viewBox="0 0 32 32" aria-hidden="true">
                <rect x="6" y="12" width="20" height="14" rx="4" fill="none" stroke="#7f7a8e" stroke-width="1.8"/>
                <path d="M11 14v-3a5 5 0 0110 0v3" fill="none" stroke="#7f7a8e" stroke-width="1.8"/>
                <circle cx="16" cy="20" r="2" fill="#7f7a8e"/>
              </svg>
              <span>Secure Payment</span>
            </div>
            <div class="security-badge" tabindex="0" :data-security-note="texts.securityNote">
              <svg width="40" height="32" viewBox="0 0 60 32" aria-hidden="true">
                <rect x="4" y="6" width="52" height="20" rx="5" fill="none" stroke="#7f7a8e" stroke-width="1.5"/>
                <text x="30" y="22" text-anchor="middle" font-size="12" font-weight="700" fill="#7f7a8e">PCI DSS</text>
              </svg>
              <span>PCI Compliant</span>
            </div>
            <div class="security-badge" tabindex="0" :data-security-note="texts.securityNote">
              <svg width="40" height="32" viewBox="0 0 60 32" aria-hidden="true">
                <rect width="60" height="32" rx="6" fill="none" stroke="#7f7a8e" stroke-width="1.5"/>
                <text x="30" y="21" text-anchor="middle" font-size="12" font-weight="700" fill="#7f7a8e">VISA</text>
              </svg>
            </div>
            <div class="security-badge" tabindex="0" :data-security-note="texts.securityNote">
              <svg width="40" height="32" viewBox="0 0 60 32" aria-hidden="true">
                <rect width="60" height="32" rx="6" fill="none" stroke="#7f7a8e" stroke-width="1.5"/>
                <circle cx="27" cy="16" r="7" fill="#7f7a8e" opacity="0.35"/>
                <circle cx="33" cy="16" r="7" fill="#7f7a8e" opacity="0.7"/>
              </svg>
            </div>
            <div class="security-badge" tabindex="0" :data-security-note="texts.securityNote">
              <svg width="40" height="32" viewBox="0 0 60 32" aria-hidden="true">
                <rect width="60" height="32" rx="6" fill="none" stroke="#7f7a8e" stroke-width="1.5"/>
                <text x="30" y="21" text-anchor="middle" font-size="12" font-weight="700" fill="#7f7a8e">PP</text>
              </svg>
            </div>
          </div>
          <div class="payment-security-text">
            <div>{{ texts.securedByPrefix }} <a tabindex="-1" href="https://www.sumit.co.il" target="_blank">{{ texts.sumitName }}</a>{{ texts.securedBySuffix }}</div>
          </div>
        </div>
      </div>
    </b-container>
    <PageFooter
      v-if="!isSubdomain"
    />
  </div>
</template>

<script>
import PageFooter from "@/components/PageFooter.vue";
import SingleRideHeader from "@/components/SingleRideHeader";
import paymentAPI from "@/api/payment";
import config from "@/common/config";
import { BIconChevronDown, BIconChevronUp } from 'bootstrap-vue';
import { mapGetters } from 'vuex';
import { translationMixin } from "@/mixins/translationMixin";
import { isValidContactPhone } from "@/common/validateContactPhone";

export default {
  name: "PaymentPage",
  mixins: [translationMixin],
  components: {
    PageFooter,
    SingleRideHeader,
    BIconChevronDown,
    BIconChevronUp
  },
  data() {
    return {
      product: {
        name: '',
        description: '',
        quantity: 1,
        unitPrice: 100,
        currency: 'ILS'
      },
      rideId: null,
      customer: {
        name: '',
        email: '',
        phone: ''
      },
      isProcessing: false,
      sumitInitialized: false,
      paymentErrorMessage: '',
      showPolicy: false
    };
  },
  computed: {
    ...mapGetters("theme", ["isSubdomain"]),
    texts() {
      return this.getPaymentTexts();
    },
    policyContent() {
      return this.getPaymentPolicyContent();
    },
    hasSumitConfig() {
      return config.config.SUMIT.COMPANY_ID && 
             config.config.SUMIT.COMPANY_ID !== 'YourCompanyID' &&
             config.config.SUMIT.API_PUBLIC_KEY && 
             config.config.SUMIT.API_PUBLIC_KEY !== 'APIPublicKey';
    },
  },
  created() {
    this.initializeProduct();
  },
  mounted() {
    // Load SUMIT payments script
    this.loadSumitScript();
  },
  methods: {
    setDefaultProduct() {
      this.product = {
        name: this.t('paymentPageSampleProduct'),
        description: this.t('paymentPageSampleProductDesc'),
        quantity: 1,
        unitPrice: 100,
        currency: 'ILS'
      };
    },
    initializeProduct() {
      this.setDefaultProduct();
      const rideId = this.$route.query.rideId || this.$route.params.rideId || null;
      if (rideId) {
        this.rideId = rideId;
        const ride = this.findRideById(rideId);
        if (ride) {
          this.product = {
            name: ride.title,
            description: ride.shortText,
            quantity: ride.quantity || 1,
            unitPrice: ride.price,
            currency: ride.currency || 'ILS',
            rideId: ride.rideId || ride.id
          };
          return;
        }
      }
    },
    findRideById(rideId) {
      const rides = this.getAllFeaturedRides();
      const numericId = parseInt(rideId);
      return rides.find(ride =>
        ride.rideId === numericId
      );
    },
    getAllFeaturedRides() {
      const pages = config.config.FEATURED_RIDES || [];
      if (!Array.isArray(pages)) {
        return [];
      }
      return pages.flatMap(page => {
        if (Array.isArray(page.rides)) {
          return page.rides;
        }
        return page ? [page] : [];
      });
    },
    loadSumitScript() {
      // First ensure jQuery is loaded
      this.ensureJQuery().then(() => {
        // Check if SUMIT script is already loaded
        if (document.querySelector('script[src="' + config.config.SUMIT.PAYMENTS_SCRIPT_URL + '"]')) {
          this.initializeSumit();
          return;
        }

        // Load SUMIT payments script
        const script = document.createElement('script');
        script.src = config.config.SUMIT.PAYMENTS_SCRIPT_URL;
        script.onload = () => {
          this.initializeSumit();
        };
        script.onerror = () => {
          this.toggleErrors(this.texts.errors.loadPaymentsScript);
        };
        document.head.appendChild(script);
      });
    },
    ensureJQuery() {
      return new Promise((resolve) => {
        // Check if jQuery is already available
        if (typeof window.jQuery !== 'undefined') {
          resolve();
          return;
        }

        // Check if jQuery script is already loading
        if (document.querySelector('script[src*="jquery"]')) {
          // Wait for it to load
          const checkInterval = setInterval(() => {
            if (typeof window.jQuery !== 'undefined') {
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);
          return;
        }

        // Load jQuery from CDN
        const jqueryScript = document.createElement('script');
        jqueryScript.src = 'https://code.jquery.com/jquery-3.6.0.min.js';
        jqueryScript.integrity = 'sha256-/xUj+3OJU5yExlq6GSYGSHk7tPXikynS7ogEvDej/m4=';
        jqueryScript.crossOrigin = 'anonymous';
        jqueryScript.onload = () => {
          resolve();
        };
        jqueryScript.onerror = () => {
          // Try alternative CDN
          const altScript = document.createElement('script');
          altScript.src = 'https://cdn.jsdelivr.net/npm/jquery@3.6.0/dist/jquery.min.js';
          altScript.onload = () => resolve();
          altScript.onerror = () => {
            this.toggleErrors(this.texts.errors.loadJquery);
            resolve(); // Continue anyway, maybe jQuery is bundled
          };
          document.head.appendChild(altScript);
        };
        document.head.appendChild(jqueryScript);
      });
    },
    initializeSumit() {
      // Wait for jQuery and OfficeGuy to be available
      if (typeof window.jQuery === 'undefined' || typeof window.OfficeGuy === 'undefined') {
        setTimeout(() => this.initializeSumit(), 100);
        return;
      }

      if (!this.hasSumitConfig) {
        this.toggleErrors(this.texts.errors.missingApiDetails);
        return;
      }

      // Initialize SUMIT Payments - matching Flask implementation
      try {
        if (window.jQuery) {
          const formElement = document.getElementById('sumit-payment-form');
          
          if (!formElement) {
            this.toggleErrors(this.texts.errors.formNotFound);
            return;
          }
          
          // Use jQuery ready to ensure DOM is ready
          window.jQuery(() => {
            // Verify form exists and is ready
            const $form = window.jQuery('#sumit-payment-form');
            if ($form.length === 0) {
              this.toggleErrors(this.texts.errors.formNotFound);
              return;
            }
            
            // Bind SUMIT to intercept form submission and call tokenization API
            window.OfficeGuy.Payments.BindFormSubmit({
              CompanyID: config.config.SUMIT.COMPANY_ID,
              APIPublicKey: config.config.SUMIT.API_PUBLIC_KEY,
              FormSelector: '#sumit-payment-form',
              ErrorsClass: '.og-errors',
              ResponseCallback: (response) => {
                this.handleTokenResponse(response);
              }
            });
            
            this.sumitInitialized = true;
          });
        }
      } catch (error) {
        console.error('Error initializing SUMIT:', error);
        this.toggleErrors(this.texts.errors.initPayments);
      }
    },
    toggleErrors(message) {
      this.paymentErrorMessage = message || '';
    },
    handleTokenResponse(response) {
      // Check for success - SUMIT can return Status as number 0 or string "Success (0)"
      const isSuccess = response && (
        response.Status === 0 || 
        response.Status === '0' ||
        response.Status === 'Success (0)' ||
        response.Success === true ||
        (response.Data && response.Data.SingleUseToken)
      );

      if (!isSuccess) {
        const message =
          (response && (response.UserErrorMessage || response.TechnicalErrorDetails || response.Message)) ||
          this.texts.errors.tokenFailed;
        this.toggleErrors(message);
        return;
      }

      this.toggleErrors('');
      if (!this.validateLocalInputs()) {
        return;
      }
      
      // Try multiple ways to extract the token
      let token = null;
      
      // Method 1: response.Data.SingleUseToken (most common)
      if (response.Data && response.Data.SingleUseToken) {
        token = response.Data.SingleUseToken;
      }
      // Method 2: response.SingleUseToken
      else if (response.SingleUseToken) {
        token = response.SingleUseToken;
      }
      // Method 3: response.Data (if it's the token itself)
      else if (response.Data && typeof response.Data === 'string') {
        token = response.Data;
      }
      // Method 4: Check form for hidden input (SUMIT might have added it)
      else {
        const form = this.$refs.paymentForm;
        if (form) {
          const tokenInput = form.querySelector('input[name="og-token"]') || 
                           form.querySelector('input[data-og="token"]') ||
                           form.querySelector('input[name="SingleUseToken"]');
          if (tokenInput && tokenInput.value) {
            token = tokenInput.value;
          }
        }
      }

      if (!token) {
        this.toggleErrors(this.texts.errors.tokenMissing);
        return;
      }
      
      // Immediately submit the charge - SUMIT tokens are time-sensitive and single-use
    this.removeSumitTokenInputs();
      this.submitCharge(token);
    },
    async submitCharge(singleUseToken) {
      const submitBtn = document.getElementById('submit-payment-btn');

      // Validate token
      if (!singleUseToken || typeof singleUseToken !== 'string' || singleUseToken.trim() === '') {
        this.toggleErrors(this.texts.errors.invalidToken);
        return;
      }

      const payload = {
        singleUseToken: singleUseToken.trim(), // Ensure no whitespace
        customer: {
          name: this.customer.name || '',
          email: this.customer.email || '',
          phone: this.customer.phone || ''
        },
        items: [
          {
            name: this.product.name,
            description: this.product.description,
            quantity: this.product.quantity,
            unitPrice: this.product.unitPrice,
            currency: this.product.currency
          }
        ],
        rideId: this.rideId
      };

      this.isProcessing = true;
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = this.texts.processingPayment;
      }
      try {
        const data = await paymentAPI.processPayment(
          singleUseToken.trim(),
          payload.customer,
          payload.items,
          {
            rideId: this.rideId,
            documentDescription: `Ride ID: ${this.rideId || 'N/A'}`
          }
        );
        // Check if response indicates success
        if (this.isSuccessfulPayment(data)) {
          const query = {
            response: JSON.stringify(data),
            rideId: this.rideId
          };
          if (data.sumitResponse) {
            query.sumitResponse = JSON.stringify(data.sumitResponse);
          }
          this.toggleErrors('');
          // Navigate to success page with response data
          this.$router.push({
            name: 'payment-success',
            query
          });
        } else {
          // Show error but don't navigate
          const errorMsg = this.extractErrorMessage(data);
          this.toggleErrors(errorMsg);
        }
      } catch (error) {
        const errorMessage = this.extractErrorMessage(error.response?.data) || 
                            error.response?.data?.message || 
                            error.message || 
                            this.texts.errors.requestFailed;
        this.toggleErrors(errorMessage);
      } finally {
        this.isProcessing = false;
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = `${this.texts.payButton} ${this.product.unitPrice} ${this.product.currency}`;
        }
      }
    },
    goHome() {
      // Prefer returning to the manager page the user came from (based on rideId).
      const owningManager = this.getOwningManager();

      if (owningManager) {
        const managerUrlName = owningManager.slug || owningManager.managerUrlName;
        if (managerUrlName) {
          this.$router.push({
            name: 'featured-rides-landing-manager',
            params: { managerUrlName }
          });
          return;
        }
      }

      // Fallback: general featured rides landing (closer to user's context than HomePage)
      this.$router.push({ name: 'featured-rides-landing' });
    },
    getOwningManager() {
      const rideId =
        this.$route.query.rideId ||
        this.$route.params.rideId ||
        this.rideId ||
        this.product?.rideId;

      if (!rideId) {
        return null;
      }

      const featuredPages = config.config.FEATURED_RIDES || [];
      const numericRideId = parseInt(rideId);
      if (!Array.isArray(featuredPages) || Number.isNaN(numericRideId)) {
        return null;
      }

      return featuredPages.find((page) => {
        if (!Array.isArray(page?.rides)) {
          return false;
        }
        return page.rides.some((ride) => ride.rideId === numericRideId);
      }) || null;
    },
    extractErrorMessage(response) {
      if (!response) {
        return this.texts.errors.genericContactManager;
      }
      const sumit = response.sumitResponse || response.sumit_response;
      const statusDescription = sumit?.Data?.Payment?.StatusDescription;
      if (statusDescription) {
        return statusDescription;
      }
      if (response.UserErrorMessage) {
        return response.UserErrorMessage;
      }
      if (response.error) {
        return response.error;
      }
      return this.texts.errors.genericContactManager;
    },
    validateLocalInputs() {
      const name = (this.customer.name || '').trim();
      if (!name) {
        this.toggleErrors(this.texts.errors.fillFullName);
        return false;
      }
      const email = (this.customer.email || '').trim();
      if (!email) {
        this.toggleErrors(this.texts.errors.enterEmail);
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.toggleErrors(this.texts.errors.invalidEmail);
        return false;
      }
      const phoneDigits = (this.customer.phone || '').replace(/\D/g, '');
      if (!isValidContactPhone(this.customer.phone)) {
        this.toggleErrors(
          phoneDigits.length < 8 ? this.texts.errors.phoneTooShort : this.texts.errors.phoneTooLong
        );
        return false;
      }
      return true;
    },
    clearPaymentError() {
      if (this.paymentErrorMessage) {
        this.paymentErrorMessage = '';
      }
      this.removeSumitTokenInputs();
    },
    removeSumitTokenInputs() {
      const form = this.$refs.paymentForm;
      if (!form) {
        return;
      }
      const tokenSelectors = [
        'input[name="og-token"]',
        'input[data-og="token"]',
        'input[name="SingleUseToken"]'
      ];
      tokenSelectors.forEach((selector) => {
        const nodes = form.querySelectorAll(selector);
        nodes.forEach(node => node.parentNode?.removeChild(node));
      });
    },
    isSuccessfulPayment(response) {
      if (!response || typeof response !== 'object') {
        return false;
      }

      if (Array.isArray(response.rideLinks) && response.rideLinks.length > 0) {
        return true;
      }

      const sumit = response.sumitResponse || response.sumit_response;
      if (sumit) {
        const payment = sumit.Data?.Payment;
        if (payment?.ValidPayment) {
          return true;
        }
        const paymentStatus = (payment?.Status || '').toString();
        if (paymentStatus === '000' || paymentStatus === 'Success (0)') {
          return true;
        }
        return false;
      }

      if (response.ValidPayment || response.Payment?.ValidPayment) {
        return true;
      }

      const status = (response.Status || '').toString();
      if (status === 'Success' || status === 'Success (0)') {
        return true;
      }

      return false;
    }
  },
  metaInfo() {
    return {
      title: this.texts.metaTitle,
      link: [
        { property: 'og:title', content: this.texts.metaTitle },
        { property: 'og:description', content: this.t('paymentPageCompletePayment') },
        { property: 'og:type', content: 'website' }
      ]
    };
  }
};
</script>

<style scoped>
.payment-content {
  min-height: 60vh;
  padding: 3rem 0;
}

.payment-form-column {
  margin-top: 2rem;
}

@media (min-width: 768px) {
  .payment-form-column {
    margin-top: 0;
  }
}

.product-info-card,
.payment-card {
  background: #fff;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  text-align: right;
  height: 100%;
}

.payment-page[dir="ltr"] .product-info-card,
.payment-page[dir="ltr"] .payment-card {
  text-align: left;
}

.payment-card .form-group:first-of-type {
  margin-top: 0.75rem;
}

.product-info-card h2 {
  font-size: 2rem;
  color: #0d2f29;
  margin-bottom: 1rem;
}

.product-info-card p {
  font-size: 1.6rem;
  color: #666;
  line-height: 1.8;
  margin-bottom: 1.5rem;
}

.panel {
  margin-top: 2rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
}

.panel-heading {
  background: #f8f9fa;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #ddd;
  font-weight: bold;
  font-size: 1.6rem;
}

.panel-body {
  padding: 1.5rem;
}

.panel-body .lead {
  font-size: 1.8rem;
  font-weight: bold;
  color: #07A0C3;
  margin-bottom: 1rem;
}

.list-unstyled {
  list-style: none;
  padding: 0;
  margin: 1rem 0;
}

.list-unstyled li {
  font-size: 1.6rem;
  margin-bottom: 0.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-size: 1.6rem;
  font-weight: 500;
  color: #333;
  margin-bottom: 0.5rem;
}

.payment-page[dir="ltr"] #sumit-payment-form label {
  text-align: left;
}

.payment-page[dir="ltr"] #sumit-payment-form .form-group {
  text-align: left;
}

.form-control {
  width: 100%;
  padding: 1rem;
  font-size: 1.6rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  direction: rtl;
  color: #333;
}

.payment-page[dir="ltr"] .form-control {
  direction: ltr;
}

.form-control::placeholder {
  color: #aaa;
}

.form-control::-webkit-input-placeholder {
  color: #aaa;
}

.form-control::-moz-placeholder {
  color: #aaa;
  opacity: 1;
}

.form-control:-ms-input-placeholder {
  color: #aaa;
}

.form-control:focus {
  outline: none;
  border-color: #07A0C3;
  box-shadow: 0 0 0 3px rgba(7, 160, 195, 0.1);
}

.form-inline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.form-inline .form-group {
  margin-bottom: 0;
}

.form-inline .form-group.mr-10 {
  margin-left: 1rem;
}

.form-inline .form-group.ml-10 {
  margin-right: 1rem;
}

.form-inline > span {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 0.75rem;
  font-size: 2.4rem;
  font-weight: 600;
  line-height: 1;
  color: #495057;
  min-width: 1.5rem;
  align-self: flex-end;
  padding-bottom: 0.5rem;
}

.form-inline .form-group label {
  margin-bottom: 0.5rem;
  margin-left: 1rem;
  display: inline-block;
  margin-right: 0.75rem;
}

.form-inline .expiration-input {
  min-width: 120px;
  width: auto;
  max-width: 150px;
  padding: 1.2rem;
  font-size: 1.8rem;
}

.og-errors {
  margin-bottom: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
}

.submit-payment-btn {
  font-size: 1.8rem;
  padding: 1.2rem 1rem;
  border-radius: 12px;
}

.submit-payment-btn span {
  font-weight: 600;
}

.processing-indicator {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-size: 1.4rem;
  font-weight: 600;
  color: #0d2f29;
}

.alert-warning {
  background-color: #fff3cd;
  border-color: #ffc107;
  color: #856404;
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
}

.alert-warning code {
  background-color: #fff;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
}

.mt-30 {
  margin-top: 2rem;
}

#payment-result {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  font-size: 1.4rem;
  max-height: 400px;
  overflow-y: auto;
  direction: ltr;
  text-align: left;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.cancellation-policy-section {
  border-top: 1px solid #e9ecef;
  padding-top: 1rem;
}

.policy-toggle-btn {
  color: #07A0C3;
  font-size: 1.4rem;
  font-weight: 600;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  text-align: right;
}

.payment-page[dir="ltr"] .policy-toggle-btn {
  text-align: left;
}

.policy-toggle-btn:hover {
  color: #34D0B6;
  text-decoration: underline;
}

.policy-content {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1.5rem;
  max-height: 500px;
  overflow-y: auto;
  font-size: 1.4rem;
  line-height: 1.8;
  text-align: right;
  direction: rtl;
}

.payment-page[dir="ltr"] .policy-content {
  text-align: left;
  direction: ltr;
}

.policy-content h5 {
  font-size: 1.8rem;
  font-weight: 700;
  color: #0d2f29;
  margin-bottom: 1rem;
}

.policy-content h6 {
  font-size: 1.6rem;
  font-weight: 600;
  color: #07A0C3;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
}

.policy-content p {
  margin-bottom: 1rem;
  color: #333;
}

.policy-content ul {
  margin-right: 2rem;
  margin-bottom: 1rem;
}

.payment-page[dir="ltr"] .policy-content ul {
  margin-right: 0;
  margin-left: 2rem;
}

.policy-content li {
  margin-bottom: 0.5rem;
}

@media (max-width: 768px) {
  .payment-content {
    padding: 2rem 1rem 3rem;
  }
}

.policy-content a {
  color: #07A0C3;
  text-decoration: underline;
}

.policy-content a:hover {
  color: #34D0B6;
}

.payment-security-section {
  border-top: 1px solid #e9ecef;
  padding-top: 1.5rem;
  margin-top: 2rem;
}

.payment-security-icons {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  margin-bottom: 1rem;
}

.security-badge {
  min-width: 110px;
  min-height: 64px;
  border: 1px solid #cfcada;
  border-radius: 12px;
  padding: 0.5rem 0.75rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fbfbfd;
  text-align: center;
  color: #7f7a8e;
  font-size: 1.1rem;
  font-weight: 600;
  gap: 0.25rem;
  position: relative;
  cursor: pointer;
}

.security-badge svg {
  display: block;
}

.security-badge span {
  font-size: 0.95rem;
}

.security-badge::after {
  content: attr(data-security-note);
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  background: #1f213a;
  color: #fff;
  padding: 0.6rem 1rem;
  border-radius: 8px;
  font-size: 1rem;
  line-height: 1.4;
  width: 220px;
  max-width: 80vw;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  box-shadow: 0 6px 18px rgba(31, 33, 58, 0.2);
  z-index: 10;
}

.security-badge::before {
  content: '';
  position: absolute;
  bottom: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  border-width: 6px;
  border-style: solid;
  border-color: #1f213a transparent transparent transparent;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 9;
}

.security-badge:hover::after,
.security-badge:hover::before,
.security-badge:focus::after,
.security-badge:focus::before,
.security-badge:active::after,
.security-badge:active::before {
  opacity: 1;
}

.payment-security-text {
  text-align: center;
  color: #7f7a8e;
  font-size: 1.3rem;
  line-height: 1.6;
}

.payment-security-text a {
  color: #7f7a8e;
  text-decoration: underline;
}

.payment-security-text a:hover {
  color: #07A0C3;
}

@media (max-width: 768px) {
  .product-info-card,
  .payment-card {
    padding: 1.5rem;
    margin-bottom: 2rem;
  }

  .policy-content {
    font-size: 1.3rem;
    padding: 1rem;
  }

  .policy-content h5 {
    font-size: 1.6rem;
  }

  .policy-content h6 {
    font-size: 1.4rem;
  }

  .payment-security-section {
    text-align: center;
  }

  .payment-security-icons {
    justify-content: center;
  }

  .payment-security-text {
    text-align: center;
  }
}
</style>
